'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuChevronDown } from 'react-icons/lu';

import { Box, BoxProps } from '../box';
import { Flex } from '../flex';
import { Text } from '../text';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Skeleton } from '../skeleton';

// Motion div for animations (using native div to avoid Chakra prop conflicts)
const MotionDiv = motion.div;

export type AccentPosition = 'left' | 'top' | 'none';
export type SectionVariant = 'default' | 'elevated' | 'outlined' | 'ghost';

export interface DetailSectionProps extends Omit<BoxProps, 'title'> {
  /** Section title */
  title: string;
  /** Section subtitle/description */
  subtitle?: string;
  /** Icon to display in header */
  icon?: React.ReactNode;
  /** Accent color (CSS color or theme token) */
  accentColor?: string;
  /** Accent position */
  accentPosition?: AccentPosition;
  /** Section variant */
  variant?: SectionVariant;
  /** Header actions (buttons, menu, etc.) */
  actions?: React.ReactNode;
  /** Footer content */
  footer?: React.ReactNode;
  /** Collapsible state */
  collapsible?: boolean;
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state */
  isCollapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state content */
  emptyState?: React.ReactNode;
  /** Show empty state when children is empty */
  isEmpty?: boolean;
  /** Animation delay for staggered entry (in seconds) */
  staggerDelay?: number;
  /** Children content */
  children: React.ReactNode;
}

/**
 * DetailSection - Enhanced section card for detail pages
 * Features accent borders, collapsible content, and smooth animations
 */
export const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  subtitle,
  icon,
  accentColor = '#3B82F6',
  accentPosition = 'left',
  variant = 'default',
  actions,
  footer,
  collapsible = false,
  defaultCollapsed = false,
  isCollapsed: controlledCollapsed,
  onCollapsedChange,
  isLoading = false,
  emptyState,
  isEmpty = false,
  staggerDelay = 0,
  children,
  ...boxProps
}) => {
  // Internal collapsed state
  const [internalCollapsed, setInternalCollapsed] = React.useState(defaultCollapsed);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const handleToggle = () => {
    if (!collapsible) return;
    const newState = !collapsed;
    if (!isControlled) {
      setInternalCollapsed(newState);
    }
    onCollapsedChange?.(newState);
  };

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: staggerDelay,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  const contentVariants = {
    collapsed: {
      height: 0,
      opacity: 0,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] },
    },
    expanded: {
      height: 'auto',
      opacity: 1,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] },
    },
  };

  const chevronVariants = {
    collapsed: { rotate: -90 },
    expanded: { rotate: 0 },
  };

  // Get variant styles
  const getVariantStyles = (): BoxProps => {
    const baseStyles: BoxProps = {
      borderRadius: 'xl',
      overflow: 'hidden',
    };

    switch (variant) {
      case 'elevated':
        return {
          ...baseStyles,
          backgroundColor: 'bg.surface',
          boxShadow: 'md',
        };
      case 'outlined':
        return {
          ...baseStyles,
          backgroundColor: 'bg.surface',
          border: '1px solid',
          borderColor: 'border.muted',
        };
      case 'ghost':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: 'bg.surface',
          border: '1px solid',
          borderColor: 'border.muted',
        };
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box position="relative" {...getVariantStyles()} {...boxProps}>
        <Box padding={5}>
          <Flex alignItems="center" gap={3} marginBottom={4}>
            <Skeleton width="32px" height="32px" borderRadius="lg" />
            <Box flex={1}>
              <Skeleton width="150px" height="20px" marginBottom={1} />
              <Skeleton width="200px" height="14px" />
            </Box>
          </Flex>
          <Box>
            <Skeleton width="100%" height="60px" marginBottom={3} />
            <Skeleton width="80%" height="40px" />
          </Box>
        </Box>
      </Box>
    );
  }

  // Accent line styles based on position
  const accentLineStyle: React.CSSProperties =
    accentPosition === 'top'
      ? {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: accentColor,
        }
      : accentPosition === 'left'
        ? {
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '4px',
            background: accentColor,
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
          }
        : {};

  return (
    <MotionDiv
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      style={{ position: 'relative' }}
    >
      <Box position="relative" {...getVariantStyles()} {...boxProps}>
        {/* Accent line */}
        {accentPosition !== 'none' && <div style={accentLineStyle} />}

        {/* Header */}
        <Box
          padding={5}
          paddingBottom={collapsed ? 5 : 3}
          cursor={collapsible ? 'pointer' : 'default'}
          onClick={collapsible ? handleToggle : undefined}
          transition="background-color 0.2s"
          userSelect={collapsible ? 'none' : 'auto'}
        >
          <Flex alignItems="center" justifyContent="space-between" gap={3}>
            {/* Left side - Icon and title */}
            <Flex alignItems="center" gap={3} flex={1} minWidth={0}>
              {icon && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  width="36px"
                  height="36px"
                  borderRadius="lg"
                  backgroundColor={`${accentColor}15`}
                  flexShrink={0}
                >
                  <Icon size="md" style={{ color: accentColor }}>
                    {icon}
                  </Icon>
                </Flex>
              )}
              <Box flex={1} minWidth={0}>
                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  color="fg.default"
                  lineHeight="tight"
                  truncate
                >
                  {title}
                </Text>
                {subtitle && (
                  <Text fontSize="sm" color="fg.muted" marginTop={0.5} lineClamp={1}>
                    {subtitle}
                  </Text>
                )}
              </Box>
            </Flex>

            {/* Right side - Actions and collapse toggle */}
            <Flex alignItems="center" gap={2} flexShrink={0}>
              {actions && (
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>{actions}</Box>
              )}
              {collapsible && (
                <MotionDiv
                  variants={chevronVariants}
                  animate={collapsed ? 'collapsed' : 'expanded'}
                  transition={{ duration: 0.2 }}
                >
                  <IconButton
                    icon={<LuChevronDown />}
                    aria-label={collapsed ? 'Expand section' : 'Collapse section'}
                    variant="ghost"
                    size="sm"
                    onClick={handleToggle}
                  />
                </MotionDiv>
              )}
            </Flex>
          </Flex>
        </Box>

        {/* Content */}
        <AnimatePresence initial={false}>
          {!collapsed && (
            <MotionDiv
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              style={{ overflow: 'hidden' }}
            >
              <Box paddingX={5} paddingBottom={5}>
                {isEmpty && emptyState ? (
                  <Box padding={6} backgroundColor="bg.subtle" borderRadius="lg" textAlign="center">
                    {emptyState}
                  </Box>
                ) : (
                  children
                )}
              </Box>

              {/* Footer */}
              {footer && (
                <Box
                  paddingX={5}
                  paddingY={3}
                  borderTop="1px solid"
                  borderColor="border.muted"
                  backgroundColor="bg.subtle"
                >
                  {footer}
                </Box>
              )}
            </MotionDiv>
          )}
        </AnimatePresence>
      </Box>
    </MotionDiv>
  );
};

export default DetailSection;
